import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Pencil } from 'lucide-react'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { DeleteIcon } from '@/components/icons'
import { useAuth } from '@/hooks/useAuth'
import { Permissions } from '@/lib/permissions'
import type { Project } from '@/types'
import { getIndustryLabel } from '@/types'
import { ProjectCollaborators } from './ProjectCollaborators'
import { EditProjectDialog } from './edit'
import { DeleteProjectDialog } from './DeleteProjectDialog'

interface ProjectTileViewProps {
  projects: Project[]
}

export function ProjectTileView({ projects }: ProjectTileViewProps) {
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const canEdit = hasPermission(Permissions.Host.Project.Update)
  const canDelete = hasPermission(Permissions.Host.Project.Delete)
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null)

  const handleEditClick = (e: React.MouseEvent, project: Project) => {
    e.stopPropagation()
    setEditingProject(project)
  }

  const handleDeleteClick = (e: React.MouseEvent, project: Project) => {
    e.stopPropagation()
    setProjectToDelete(project)
    setDeleteDialogOpen(true)
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="projects-tile-grid">
        {projects.map((project) => (
          <Card
            key={project.id}
            className="flex cursor-pointer flex-col transition-shadow hover:shadow-md relative"
            data-testid="project-card"
            onClick={() => navigate(`/projects/${project.id}`)}
          >
            {(canEdit || canDelete) && (
              <div className="absolute top-2 right-2 flex items-center gap-1">
                {canEdit && (
                  <Button
                    variant="ghost"
                    size="iconSm"
                    className="text-muted-foreground"
                    aria-label="Edit project"
                    onClick={(e) => handleEditClick(e, project)}
                  >
                    <Pencil className="h-4 w-4" />
                    <span className="sr-only">Edit project</span>
                  </Button>
                )}
                {canDelete && (
                  <Button
                    variant="ghost"
                    size="iconSm"
                    className="text-muted-foreground"
                    aria-label="Delete project"
                    onClick={(e) => handleDeleteClick(e, project)}
                  >
                    <DeleteIcon className="h-5 w-5" />
                    <span className="sr-only">Delete project</span>
                  </Button>
                )}
              </div>
            )}
            <CardHeader className="flex-1 pb-3">
              <CardTitle className="text-base break-all">{project.name}</CardTitle>
              {(project.client || project.type || project.industry) && (
                <p className="text-sm text-muted-foreground">
                  {[project.client, project.type, getIndustryLabel(project.industry)].filter(Boolean).join(' · ')}
                </p>
              )}
            </CardHeader>
            <div className="px-6 pb-4 space-y-3">
              {project.teamMembers && project.teamMembers.length > 0 && (
                <ProjectCollaborators members={project.teamMembers} />
              )}
            </div>
          </Card>
        ))}
      </div>

      {editingProject && (
        <EditProjectDialog
          project={editingProject}
          open={!!editingProject}
          onOpenChange={(open) => !open && setEditingProject(null)}
        />
      )}

      {projectToDelete && (
        <DeleteProjectDialog
          project={projectToDelete}
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
        />
      )}
    </>
  )
}
